import React, { useState, useEffect, useRef } from 'react';
import { Plus, Pencil, Trash2, Save, X, Upload, Image } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GalleryAdmin = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    before_image: '',
    after_image: '',
    item_type: 'mattress',
    description: ''
  });
  const [beforePreview, setBeforePreview] = useState('');
  const [afterPreview, setAfterPreview] = useState('');
  
  const beforeInputRef = useRef(null);
  const afterInputRef = useRef(null);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await fetch(`${API_URL}/api/gallery?active_only=false`);
      if (response.ok) {
        const data = await response.json();
        setItems(data);
      }
    } catch (error) {
      console.error('Error fetching gallery:', error);
      toast.error('Eroare la încărcarea galeriei');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Fișierul este prea mare. Maxim 5MB.');
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result;
      if (type === 'before') {
        setFormData({ ...formData, before_image: base64 });
        setBeforePreview(base64);
      } else {
        setFormData({ ...formData, after_image: base64 });
        setAfterPreview(base64);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.before_image || !formData.after_image || !formData.description) {
      toast.error('Completează toate câmpurile și încarcă ambele fotografii');
      return;
    }

    try {
      const url = editingId 
        ? `${API_URL}/api/gallery/${editingId}`
        : `${API_URL}/api/gallery`;
      
      const method = editingId ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingId ? 'Imagine actualizată!' : 'Imagine adăugată!');
        resetForm();
        fetchItems();
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast.error('Eroare la salvare');
    }
  };

  const handleEdit = (item) => {
    setFormData({
      before_image: item.before_image,
      after_image: item.after_image,
      item_type: item.item_type,
      description: item.description
    });
    setBeforePreview(item.before_image);
    setAfterPreview(item.after_image);
    setEditingId(item.id);
    setShowAddForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Ești sigur că vrei să ștergi această imagine?')) return;

    try {
      const response = await fetch(`${API_URL}/api/gallery/${id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('Imagine ștearsă');
        fetchItems();
      }
    } catch (error) {
      toast.error('Eroare la ștergere');
    }
  };

  const resetForm = () => {
    setFormData({
      before_image: '',
      after_image: '',
      item_type: 'mattress',
      description: ''
    });
    setBeforePreview('');
    setAfterPreview('');
    setEditingId(null);
    setShowAddForm(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2196f3]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#0d2e5d]">Galerie Înainte & După</h2>
          <p className="text-gray-600 text-sm">Încarcă fotografiile tale reale</p>
        </div>
        <Button onClick={() => setShowAddForm(true)} className="bg-[#5cd26f] hover:bg-[#4bc160]">
          <Plus className="w-4 h-4 mr-2" />
          Adaugă Imagine
        </Button>
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <Card className="border-2 border-[#2196f3]">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>{editingId ? 'Editează Imaginea' : 'Adaugă Imagine Nouă'}</span>
              <Button variant="ghost" size="icon" onClick={resetForm}>
                <X className="w-5 h-5" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Image Upload Section */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Before Image */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fotografie ÎNAINTE *
                  </label>
                  <div 
                    className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-[#2196f3] transition-colors"
                    onClick={() => beforeInputRef.current?.click()}
                  >
                    {beforePreview ? (
                      <img 
                        src={beforePreview} 
                        alt="Before preview" 
                        className="max-h-48 mx-auto rounded-lg"
                      />
                    ) : (
                      <div className="py-8">
                        <Upload className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                        <p className="text-gray-500">Click pentru a încărca</p>
                        <p className="text-gray-400 text-xs mt-1">JPG, PNG (max 5MB)</p>
                      </div>
                    )}
                  </div>
                  <input
                    ref={beforeInputRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, 'before')}
                    className="hidden"
                  />
                  {beforePreview && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setBeforePreview('');
                        setFormData({ ...formData, before_image: '' });
                      }}
                      className="mt-2 text-red-500 hover:text-red-700"
                    >
                      Șterge imaginea
                    </Button>
                  )}
                </div>

                {/* After Image */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fotografie DUPĂ *
                  </label>
                  <div 
                    className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-[#5cd26f] transition-colors"
                    onClick={() => afterInputRef.current?.click()}
                  >
                    {afterPreview ? (
                      <img 
                        src={afterPreview} 
                        alt="After preview" 
                        className="max-h-48 mx-auto rounded-lg"
                      />
                    ) : (
                      <div className="py-8">
                        <Upload className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                        <p className="text-gray-500">Click pentru a încărca</p>
                        <p className="text-gray-400 text-xs mt-1">JPG, PNG (max 5MB)</p>
                      </div>
                    )}
                  </div>
                  <input
                    ref={afterInputRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, 'after')}
                    className="hidden"
                  />
                  {afterPreview && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setAfterPreview('');
                        setFormData({ ...formData, after_image: '' });
                      }}
                      className="mt-2 text-red-500 hover:text-red-700"
                    >
                      Șterge imaginea
                    </Button>
                  )}
                </div>
              </div>

              {/* Type and Description */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tip
                  </label>
                  <select
                    value={formData.item_type}
                    onChange={(e) => setFormData({ ...formData, item_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2196f3]"
                  >
                    <option value="mattress">Saltea</option>
                    <option value="couch">Canapea</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descriere *
                  </label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="ex: Saltea cu pete de cafea - curățată"
                    required
                  />
                </div>
              </div>

              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={resetForm}>
                  Anulează
                </Button>
                <Button type="submit" className="bg-[#2196f3] hover:bg-[#1976d2]">
                  <Save className="w-4 h-4 mr-2" />
                  {editingId ? 'Salvează' : 'Adaugă'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Gallery Items List */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-700">
          Imagini în galerie ({items.length})
        </h3>
        
        {items.length === 0 ? (
          <Card className="p-8 text-center">
            <Image className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500">Nu există imagini în galerie.</p>
            <p className="text-gray-400 text-sm mt-2">
              Adaugă prima ta fotografie înainte & după.
            </p>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {items.map((item) => (
              <Card key={item.id} className={`overflow-hidden ${!item.is_active ? 'opacity-50' : ''}`}>
                <div className="grid grid-cols-2 gap-1">
                  <div className="relative">
                    <img 
                      src={item.before_image} 
                      alt="Before" 
                      className="w-full h-32 object-cover"
                    />
                    <span className="absolute top-2 left-2 bg-red-500 text-white text-xs px-2 py-1 rounded">
                      Înainte
                    </span>
                  </div>
                  <div className="relative">
                    <img 
                      src={item.after_image} 
                      alt="After" 
                      className="w-full h-32 object-cover"
                    />
                    <span className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded">
                      După
                    </span>
                  </div>
                </div>
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-700">{item.description}</p>
                      <p className="text-xs text-gray-500">
                        {item.item_type === 'mattress' ? 'Saltea' : 'Canapea'}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(item)}
                        className="h-8 w-8 hover:bg-blue-50 hover:text-[#2196f3]"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(item.id)}
                        className="h-8 w-8 hover:bg-red-50 hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GalleryAdmin;
